import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronRight,
  FilePlus,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  Lock,
  MoreHorizontal,
  MoveRight,
  Pencil,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchInput } from "@/components/SearchInput";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useSidebar } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useCanManageAdminOnly } from "@/components/ManagersOnly";
import {
  type GlobalDocument,
  useCreateGlobalDocument,
  useDeleteGlobalDocument,
  useGlobalDocuments,
  useUpdateGlobalDocument,
} from "@/hooks/useGlobalDocuments";
import { AppLink } from "@/navigation/AppLink";
import { docHref } from "@/navigation/hrefs";
import type { ContextParams } from "@/navigation/types";
import {
  type DocsTreeNode,
  MANAGEMENT_FOLDER_ID,
  ancestorFolderIds,
  buildDocsTree,
  isDescendant,
  managementFolder,
  moveTarget,
} from "./docsTree";

const EXPANDED_STORAGE_KEY = "docs-tree:expanded";

function readExpanded(): Set<string> {
  try {
    const raw = sessionStorage.getItem(EXPANDED_STORAGE_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

/** Id of the document open in the main view, from `/docs/:docId`. */
export function useSelectedDocId() {
  const location = useLocation();
  return (
    matchPath({ path: "/docs/:docId", end: false }, location.pathname)?.params
      .docId ?? null
  );
}

// ── Move-to dialog (for mobile / context menu) ──

function MoveToDialog({
  open,
  onOpenChange,
  docs,
  nodeId,
  onMove,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  docs: GlobalDocument[];
  nodeId: string;
  onMove: (nodeId: string, newParentId: string | null) => void;
}) {
  const canSeeManagement = useCanManageAdminOnly();
  const folders = useMemo(() => {
    const movable = (d: GlobalDocument) =>
      d.is_folder && d.id !== nodeId && !isDescendant(docs, nodeId, d.id);
    const teamFolders = docs.filter((d) => d.bucket === "team" && movable(d));
    if (!canSeeManagement) return teamFolders;
    return [
      ...teamFolders,
      managementFolder([]),
      ...docs.filter((d) => d.bucket === "managers" && movable(d)),
    ];
  }, [canSeeManagement, docs, nodeId]);

  const node = docs.find((d) => d.id === nodeId);
  const currentParentId =
    node?.bucket === "managers" && !node.parent_id
      ? MANAGEMENT_FOLDER_ID
      : (node?.parent_id ?? null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xs">
        <DialogHeader>
          <DialogTitle>Move to...</DialogTitle>
        </DialogHeader>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          <button
            className={cn(
              "w-full text-left text-sm px-3 py-2 rounded-md hover:bg-accent flex items-center gap-2",
              currentParentId === null && "bg-accent",
            )}
            onClick={() => {
              onMove(nodeId, null);
              onOpenChange(false);
            }}
          >
            <FileText className="h-4 w-4 text-muted-foreground" />
            Root
          </button>
          {folders.map((f) => (
            <button
              key={f.id}
              className={cn(
                "w-full text-left text-sm px-3 py-2 rounded-md hover:bg-accent flex items-center gap-2",
                currentParentId === f.id && "bg-accent",
              )}
              onClick={() => {
                onMove(nodeId, f.id);
                onOpenChange(false);
              }}
            >
              {f.id === MANAGEMENT_FOLDER_ID ? (
                <Lock className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Folder
                  className={cn(
                    "h-4 w-4 text-muted-foreground",
                    f.bucket === "managers" && "ml-4",
                  )}
                />
              )}
              {f.title}
            </button>
          ))}
          {folders.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">
              No folders available
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Tree Item ──

interface TreeHandlers {
  selectedId: string | null;
  selectedFolderId: string | null;
  expandedFolders: Set<string>;
  onOpenDoc: (doc: GlobalDocument) => void;
  onToggleFolder: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
  onCreateIn: (parentId: string, isFolder: boolean) => void;
  onMove: (nodeId: string, newParentId: string | null) => void;
  dragState: { draggingId: string | null; dropTargetId: string | null };
  onDragStart: (id: string) => void;
  onDragOver: (e: React.DragEvent, id: string, isFolder: boolean) => void;
  onDragLeave: () => void;
  onDrop: (
    e: React.DragEvent,
    targetId: string | null,
    isFolder: boolean,
  ) => void;
  onDragEnd: () => void;
  docs: GlobalDocument[];
}

function TreeItem({
  node,
  level,
  handlers,
}: {
  node: DocsTreeNode;
  level: number;
  handlers: TreeHandlers;
}) {
  const {
    selectedId,
    selectedFolderId,
    expandedFolders,
    onOpenDoc,
    onToggleFolder,
    onRename,
    onDelete,
    onCreateIn,
    onMove,
    dragState,
    onDragStart,
    onDragOver,
    onDragLeave,
    onDrop,
    onDragEnd,
    docs,
  } = handlers;
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(node.title);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isExpanded = expandedFolders.has(node.id);
  const isSelected = node.is_folder
    ? selectedFolderId === node.id
    : selectedId === node.id;
  const isDragging = dragState.draggingId === node.id;
  const isDropTarget = dragState.dropTargetId === node.id;
  const isManagementFolder = node.id === MANAGEMENT_FOLDER_ID;

  useEffect(() => {
    setTitle(node.title);
  }, [node.title]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const handleRename = () => {
    const trimmed = title.trim();
    if (trimmed && trimmed !== node.title) {
      onRename(node.id, trimmed);
    } else {
      setTitle(node.title);
    }
    setEditing(false);
  };

  const icon = isManagementFolder ? (
    <Lock className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
  ) : node.is_folder ? (
    isExpanded ? (
      <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
    ) : (
      <Folder className="h-4 w-4 text-muted-foreground shrink-0" />
    )
  ) : (
    <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
  );

  const label = editing ? (
    <Input
      ref={inputRef}
      value={title}
      onChange={(e) => setTitle(e.target.value)}
      onBlur={handleRename}
      onKeyDown={(e) => {
        if (e.key === "Enter") handleRename();
        if (e.key === "Escape") {
          setTitle(node.title);
          setEditing(false);
        }
      }}
      className="h-6 text-sm flex-1 py-0"
    />
  ) : (
    <span className="truncate flex-1">{node.title}</span>
  );

  return (
    <div>
      <div
        className={cn(
          "group flex items-center gap-1 pr-1 rounded-md text-sm select-none",
          "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
          isSelected &&
            "bg-sidebar-accent text-sidebar-accent-foreground font-medium",
          isDragging && "opacity-40",
          isDropTarget &&
            node.is_folder &&
            "ring-2 ring-primary ring-inset bg-primary/10",
        )}
        draggable={!editing && !isManagementFolder}
        data-testid={
          isManagementFolder ? "docs-management-folder" : "docs-tree-item"
        }
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", node.id);
          onDragStart(node.id);
        }}
        onDragOver={(e) => onDragOver(e, node.id, node.is_folder)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, node.id, node.is_folder)}
        onDragEnd={onDragEnd}
      >
        {node.is_folder ? (
          <button
            type="button"
            className="flex flex-1 min-w-0 items-center gap-1 py-1 text-left cursor-pointer"
            style={{ paddingLeft: `${level * 12 + 6}px` }}
            aria-expanded={isExpanded}
            onClick={() => onToggleFolder(node.id)}
          >
            <ChevronRight
              className={cn(
                "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
                isExpanded && "rotate-90",
              )}
            />
            {icon}
            {label}
          </button>
        ) : editing ? (
          <div
            className="flex flex-1 min-w-0 items-center gap-1 py-1"
            style={{ paddingLeft: `${level * 12 + 6}px` }}
          >
            <span className="w-3.5 shrink-0" />
            {icon}
            {label}
          </div>
        ) : (
          <AppLink
            to={docHref(node.id)}
            draggable={false}
            className="flex flex-1 min-w-0 items-center gap-1 py-1"
            style={{ paddingLeft: `${level * 12 + 6}px` }}
            aria-current={isSelected ? "page" : undefined}
            onClick={(e) => {
              if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
                onOpenDoc(node);
              }
            }}
          >
            <span className="w-3.5 shrink-0" />
            {icon}
            {label}
          </AppLink>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
              aria-label="Item actions"
              title="Actions"
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {!isManagementFolder && (
              <>
                <DropdownMenuItem onClick={() => setEditing(true)}>
                  <Pencil className="h-3.5 w-3.5 mr-2" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setMoveDialogOpen(true)}>
                  <MoveRight className="h-3.5 w-3.5 mr-2" />
                  Move to...
                </DropdownMenuItem>
              </>
            )}
            {node.is_folder && (
              <>
                <DropdownMenuItem onClick={() => onCreateIn(node.id, false)}>
                  <FilePlus className="h-3.5 w-3.5 mr-2" />
                  New Document
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onCreateIn(node.id, true)}>
                  <FolderPlus className="h-3.5 w-3.5 mr-2" />
                  New Folder
                </DropdownMenuItem>
              </>
            )}
            {!isManagementFolder && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <DropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Delete {node.is_folder ? "folder" : "document"}?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {node.is_folder
                        ? "This will permanently delete this folder and all its contents."
                        : "This will permanently delete this document."}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => onDelete(node.id)}>
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <MoveToDialog
        open={moveDialogOpen}
        onOpenChange={setMoveDialogOpen}
        docs={docs}
        nodeId={node.id}
        onMove={onMove}
      />

      {node.is_folder && isExpanded && (
        <div>
          {node.children.length === 0 ? (
            <div
              className="text-xs text-muted-foreground italic py-1"
              style={{ paddingLeft: `${(level + 1) * 12 + 24}px` }}
            >
              Empty folder
            </div>
          ) : (
            node.children.map((child) => (
              <TreeItem
                key={child.id}
                node={child}
                level={level + 1}
                handlers={handlers}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The Docs context navigation (D8): the document tree with folders, the
 * Management bucket for admins, search, create, rename, move and
 * drag-and-drop. Documents are links to `/docs/<id>`.
 */
export function DocsTreeNav({
  collapsed,
}: {
  params: ContextParams;
  collapsed: boolean;
}) {
  const { data: docs = [], isLoading } = useGlobalDocuments();
  const canSeeManagement = useCanManageAdminOnly();
  const createDoc = useCreateGlobalDocument();
  const updateDoc = useUpdateGlobalDocument();
  const deleteDoc = useDeleteGlobalDocument();
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const selectedId = useSelectedDocId();

  const [expandedFolders, setExpandedFolders] =
    useState<Set<string>>(readExpanded);
  useEffect(() => {
    try {
      sessionStorage.setItem(
        EXPANDED_STORAGE_KEY,
        JSON.stringify([...expandedFolders]),
      );
    } catch {
      // Storage unavailable: the tree just forgets its state.
    }
  }, [expandedFolders]);

  // Folder that "Doc" / "Folder" create into.
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

  const [dragState, setDragState] = useState<{
    draggingId: string | null;
    dropTargetId: string | null;
  }>({ draggingId: null, dropTargetId: null });

  const [search, setSearch] = useState("");

  const tree = useMemo(
    () => buildDocsTree(docs, canSeeManagement),
    [docs, canSeeManagement],
  );

  // Filter tree: match docs by title, include ancestor folders
  const filteredTree = useMemo(() => {
    if (!search.trim()) return tree;
    const q = search.toLowerCase();
    const matchingIds = new Set(
      docs
        .filter((d) => !d.is_folder && d.title.toLowerCase().includes(q))
        .map((d) => d.id),
    );
    const includedIds = new Set(matchingIds);
    for (const id of matchingIds) {
      let current = docs.find((d) => d.id === id);
      while (current?.parent_id) {
        includedIds.add(current.parent_id);
        current = docs.find((d) => d.id === current!.parent_id);
      }
    }
    return buildDocsTree(
      docs.filter((d) => includedIds.has(d.id)),
      canSeeManagement &&
        docs.some((d) => d.bucket === "managers" && matchingIds.has(d.id)),
    );
  }, [tree, docs, search, canSeeManagement]);

  // Search expands every folder.
  const searchExpandedFolders = useMemo(() => {
    if (!search.trim()) return null;
    return new Set([
      MANAGEMENT_FOLDER_ID,
      ...docs.filter((d) => d.is_folder).map((d) => d.id),
    ]);
  }, [search, docs]);

  // Expand the folders containing the open document.
  useEffect(() => {
    if (!selectedId) return;
    const parents = ancestorFolderIds(docs, selectedId);
    if (parents.length === 0) return;
    setExpandedFolders((prev) => {
      if (parents.every((p) => prev.has(p))) return prev;
      const next = new Set(prev);
      parents.forEach((p) => next.add(p));
      return next;
    });
  }, [selectedId, docs]);

  const closeMobileSheet = useCallback(() => {
    if (isMobile) setOpenMobile(false);
  }, [isMobile, setOpenMobile]);

  const openDoc = useCallback(
    (doc: GlobalDocument) => {
      setSelectedFolderId(
        doc.parent_id ??
          (doc.bucket === "managers" ? MANAGEMENT_FOLDER_ID : null),
      );
      closeMobileSheet();
    },
    [closeMobileSheet],
  );

  const toggleFolder = useCallback((id: string) => {
    setSelectedFolderId(id);
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleCreate = useCallback(
    (parentId: string | null, isFolder: boolean) => {
      createDoc.mutate(
        {
          title: isFolder ? "New Folder" : "Untitled Document",
          parent_id: moveTarget(parentId).parentId,
          is_folder: isFolder,
          bucket:
            parentId === MANAGEMENT_FOLDER_ID ||
            docs.find((d) => d.id === parentId)?.bucket === "managers"
              ? "managers"
              : "team",
        },
        {
          onSuccess: (doc) => {
            if (parentId) {
              setExpandedFolders((prev) => new Set(prev).add(parentId));
            }
            if (!isFolder) {
              navigate(docHref(doc.id));
              closeMobileSheet();
            }
          },
        },
      );
    },
    [closeMobileSheet, createDoc, docs, navigate],
  );

  const handleRename = useCallback(
    (id: string, title: string) => {
      updateDoc.mutate({ id, title });
    },
    [updateDoc],
  );

  const handleDelete = useCallback(
    (id: string) => {
      if (
        selectedId &&
        (selectedId === id || isDescendant(docs, id, selectedId))
      ) {
        navigate("/docs", { replace: true });
      }
      deleteDoc.mutate(id);
    },
    [deleteDoc, docs, navigate, selectedId],
  );

  const handleMove = useCallback(
    (nodeId: string, targetFolderId: string | null) => {
      const node = docs.find((d) => d.id === nodeId);
      if (!node) return;
      const target = moveTarget(targetFolderId);
      const newParentId = target.parentId;
      // Folders decide the bucket; the root/Management folder decide it here.
      const bucket = newParentId
        ? (docs.find((d) => d.id === newParentId)?.bucket ?? "team")
        : target.bucket;
      // Don't move to same parent
      if ((node.parent_id ?? null) === newParentId && node.bucket === bucket)
        return;
      // Don't move into self or descendant
      if (newParentId && isDescendant(docs, nodeId, newParentId)) return;

      updateDoc.mutate({
        id: nodeId,
        parent_id: newParentId,
        ...(bucket !== node.bucket ? { bucket } : {}),
      });

      if (targetFolderId) {
        setExpandedFolders((prev) => new Set(prev).add(targetFolderId));
      }
    },
    [docs, updateDoc],
  );

  // ── Drag handlers ──

  const handleDragStart = useCallback((id: string) => {
    setDragState({ draggingId: id, dropTargetId: null });
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, targetId: string, isFolder: boolean) => {
      e.preventDefault();
      e.stopPropagation();
      if (!dragState.draggingId || dragState.draggingId === targetId) return;
      // Only folders can be drop targets (or root)
      if (!isFolder) return;
      // Prevent dropping on own descendant
      if (isDescendant(docs, dragState.draggingId, targetId)) return;
      e.dataTransfer.dropEffect = "move";
      setDragState((prev) => ({ ...prev, dropTargetId: targetId }));
    },
    [dragState.draggingId, docs],
  );

  const handleDragLeave = useCallback(() => {
    setDragState((prev) => ({ ...prev, dropTargetId: null }));
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, targetId: string | null, isFolder: boolean) => {
      e.preventDefault();
      e.stopPropagation();
      const draggedId = dragState.draggingId;
      setDragState({ draggingId: null, dropTargetId: null });

      if (!draggedId || draggedId === targetId) return;
      if (targetId && !isFolder) return;

      handleMove(draggedId, targetId);
    },
    [dragState.draggingId, handleMove],
  );

  const handleDragEnd = useCallback(() => {
    setDragState({ draggingId: null, dropTargetId: null });
  }, []);

  // Dropping on the empty tree area moves to the root.
  const handleRootDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const draggedId = dragState.draggingId;
      setDragState({ draggingId: null, dropTargetId: null });
      if (!draggedId) return;
      handleMove(draggedId, null);
    },
    [dragState.draggingId, handleMove],
  );

  const handleRootDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!dragState.draggingId) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    },
    [dragState.draggingId],
  );

  const handlers: TreeHandlers = {
    selectedId,
    selectedFolderId,
    expandedFolders: searchExpandedFolders ?? expandedFolders,
    onOpenDoc: openDoc,
    onToggleFolder: toggleFolder,
    onRename: handleRename,
    onDelete: handleDelete,
    onCreateIn: handleCreate,
    onMove: handleMove,
    dragState,
    onDragStart: handleDragStart,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
    onDragEnd: handleDragEnd,
    docs,
  };

  // Icon rail: the header already shows the Docs icon.
  if (collapsed) return null;

  return (
    <div className="flex flex-1 flex-col min-h-0" data-testid="docs-tree">
      <div className="flex items-center gap-1 px-2 pt-2">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs px-2"
          onClick={() => handleCreate(selectedFolderId, false)}
        >
          <FilePlus className="h-3.5 w-3.5 mr-1" />
          Doc
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs px-2"
          onClick={() => handleCreate(selectedFolderId, true)}
        >
          <FolderPlus className="h-3.5 w-3.5 mr-1" />
          Folder
        </Button>
        {selectedFolderId && (
          <span className="text-xs text-muted-foreground truncate ml-1">
            in{" "}
            {selectedFolderId === MANAGEMENT_FOLDER_ID
              ? "Management"
              : (docs.find((d) => d.id === selectedFolderId)?.title ??
                "folder")}
          </span>
        )}
      </div>
      <div className="px-2 py-1.5">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search pages..."
        />
      </div>
      <div
        className="flex-1 px-2 pb-2"
        onDragOver={handleRootDragOver}
        onDrop={handleRootDrop}
      >
        {isLoading ? (
          <div className="space-y-2 px-2 py-1">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-36" />
          </div>
        ) : filteredTree.length === 0 ? (
          <div className="flex flex-col items-center text-center px-4 py-8">
            <FileText className="h-8 w-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">
              {search ? "No pages match your search." : "No documents yet"}
            </p>
            {!search && (
              <p className="text-xs text-muted-foreground mt-1">
                Create a document or folder to get started.
              </p>
            )}
          </div>
        ) : (
          filteredTree.map((node) => (
            <TreeItem key={node.id} node={node} level={0} handlers={handlers} />
          ))
        )}
      </div>
    </div>
  );
}
